/**
 * Standard PDP Recharge widget polish — mirrors starter-pack card UX.
 * - Theme metafields (banner, included items, footnote) via subscription-product-merchandising snippet
 * - Hides nested learn-more / frequency labels not exposed to ::part()
 * - Injects one-time price as compare-at above subscription price
 */
(function () {
  const WIDGET_SELECTOR = '.product-information:not(:has(.starter-pack-selector)) recharge-subscription-widget';
  const MERCH_ROOT_SELECTOR = '[data-subscription-product-merchandising]';
  const MERCH_STYLE_ID = 'rd-rc-merch-styles';
  const PLANS_SELECT_STYLE_ID = 'rd-rc-plans-select-styles';
  const PLANS_SELECT_CHEVRON =
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='30' height='30' viewBox='0 0 30 30'%3E%3Ccircle cx='15' cy='15' r='15' fill='%23000'/%3E%3Cpath d='M11 12.5L15 16.5L19 12.5' stroke='%23fff' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E\")";

  const PLANS_SELECT_STYLES = `
    [part="rc-plans-select"],
    select {
      --rd-sp-frequency-chevron-inset: 6px;
      --rd-sp-frequency-chevron-size: 30px;
      --rd-sp-frequency-chevron-gap: 6px;
      appearance: none !important;
      box-sizing: border-box !important;
      width: 100% !important;
      max-width: 100% !important;
      height: 42px !important;
      min-height: 42px !important;
      padding: 0 calc(var(--rd-sp-frequency-chevron-inset) + var(--rd-sp-frequency-chevron-size) + var(--rd-sp-frequency-chevron-gap)) 0 0.95rem !important;
      border-radius: 999px !important;
      border: 1px solid rgba(0, 0, 0, 0.25) !important;
      background-color: #fff !important;
      background-image: ${PLANS_SELECT_CHEVRON} !important;
      background-repeat: no-repeat !important;
      background-size: var(--rd-sp-frequency-chevron-size) var(--rd-sp-frequency-chevron-size) !important;
      background-position: right var(--rd-sp-frequency-chevron-inset) center !important;
      background-clip: padding-box !important;
      color: #000 !important;
      font-family: var(--font-body--family) !important;
      font-style: var(--font-body--style) !important;
      font-weight: var(--font-body--weight) !important;
      font-size: var(--font-size--paragraph-small) !important;
      line-height: var(--font-paragraph-small--line-height) !important;
      -webkit-font-smoothing: antialiased;
      cursor: pointer;
    }

    @media (max-width: 767.98px) {
      [part="rc-plans-select"],
      select {
        line-height: normal !important;
      }
    }
  `;

  const MERCH_STYLES = `
    .rd-rc-merch-banner {
      padding: 8px;
      text-align: center;
      background: #f2eee0;
      color: #000;
      font-family: var(--font-body--family);
      font-style: var(--font-body--style);
      font-weight: 700;
      font-size: var(--font-size--caption-1);
      line-height: var(--font-caption-1--line-height);
      text-transform: uppercase;
      -webkit-font-smoothing: antialiased;
    }
    .rd-rc-merch-banner.is-selected {
      background: #df552d;
      color: #fff;
    }
    .rd-rc-merch-badge {
      align-self: flex-start;
    }
    .rd-rc-merch-included-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .rd-rc-merch-included-item {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      color: #000;
      font-family: var(--font-body--family);
      font-style: var(--font-body--style);
      font-weight: var(--font-body--weight);
      font-size: var(--font-size--paragraph-small);
      line-height: var(--font-paragraph-small--line-height);
      -webkit-font-smoothing: antialiased;
    }
    .rd-rc-merch-included-item .text-body-2 {
      color: #000;
      font-family: inherit;
      font-style: inherit;
      font-weight: inherit;
      font-size: inherit;
      line-height: inherit;
    }
    .rd-rc-merch-included-item .icon--metaobject-file,
    .rd-rc-merch-included-item .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      color: #df552d;
    }
    .rd-rc-merch-included-item .icon.icon--metaobject-file {
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    .rd-rc-merch-included-item .icon-metaobject__img {
      width: 20px;
      height: 20px;
      object-fit: contain;
      filter: brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(1500%) hue-rotate(346deg)
        brightness(98%) contrast(95%);
    }
    .rd-rc-merch-included-item .icon svg {
      width: 20px;
      height: 20px;
    }
    .rd-rc-merch-footnote {
      margin: 0;
      font-family: var(--font-body--family);
      font-style: var(--font-body--style);
      font-weight: var(--font-body--weight);
      font-size: var(--font-size--paragraph-small);
      line-height: var(--font-paragraph-small--line-height);
      color: rgb(0 0 0 / 0.65);
      -webkit-font-smoothing: antialiased;
    }
    [data-rd-rc-merch-banner],
    [data-rd-rc-merch-badge],
    [data-rd-rc-merch-items],
    [data-rd-rc-merch-footnote] {
      width: 100%;
      box-sizing: border-box;
    }
    [data-rd-rc-merch-badge] {
      order: 2;
    }
    [data-rd-rc-merch-items] {
      order: 3;
    }
    [data-rd-rc-merch-footnote] {
      order: 4;
    }
  `;

  /** @returns {Record<string, string>} */
  function readMerchTemplates() {
    const root = document.querySelector(MERCH_ROOT_SELECTOR);
    if (!root) return {};

    /** @type {Record<string, string>} */
    const templates = {};
    const banner = root.querySelector('template[data-rd-rc-merch-banner]');
    const badge = root.querySelector('template[data-rd-rc-merch-badge]');
    const items = root.querySelector('template[data-rd-rc-merch-items]');
    const footnote = root.querySelector('template[data-rd-rc-merch-footnote]');

    if (banner) templates.banner = banner.innerHTML.trim();
    if (badge) templates.badge = badge.innerHTML.trim();
    if (items) templates.items = items.innerHTML.trim();
    if (footnote) templates.footnote = footnote.innerHTML.trim();

    return templates;
  }

  /** @param {ShadowRoot} root */
  function ensurePlansSelectStyles(root) {
    if (!root || root.getElementById?.(PLANS_SELECT_STYLE_ID) || root.querySelector(`#${PLANS_SELECT_STYLE_ID}`)) {
      return;
    }
    const style = document.createElement('style');
    style.id = PLANS_SELECT_STYLE_ID;
    style.textContent = PLANS_SELECT_STYLES;
    root.appendChild(style);
  }

  /** @param {ShadowRoot} root */
  function ensureMerchStyles(root) {
    if (root.getElementById?.(MERCH_STYLE_ID) || root.querySelector(`#${MERCH_STYLE_ID}`)) return;
    const style = document.createElement('style');
    style.id = MERCH_STYLE_ID;
    style.textContent = MERCH_STYLES;
    root.appendChild(style);
  }

  /**
   * @param {Element} host
   * @param {string} slotName data-rd-rc-merch-*
   * @param {string} html
   * @param {Element} parent
   * @param {Element | null} before
   */
  function upsertMerchSlot(host, slotName, html, parent, before) {
    if (!html) return null;

    const attr = `data-rd-rc-merch-${slotName}`;
    let slot = host.querySelector(`[${attr}]`);
    if (!slot) {
      slot = document.createElement('div');
      slot.setAttribute(attr, '');
      if (before) {
        parent.insertBefore(slot, before);
      } else {
        parent.appendChild(slot);
      }
    }

    if (slot.innerHTML !== html) {
      slot.innerHTML = html;
    }

    return slot;
  }

  /** @param {Element} widget */
  function enhanceMerchandising(widget) {
    const templates = readMerchTemplates();
    if (!Object.keys(templates).length) return;

    const root = widget.shadowRoot;
    if (!root) return;

    ensureMerchStyles(root);

    const subCard = root.querySelector('[part*="subscription"]');
    const subContainer = root.querySelector('[part="rc-purchase-option__sub-container"]');
    if (!subCard || !subContainer) return;

    const isSelected = (subCard.getAttribute('part') || '').includes('selected');
    const rechargeBadge = root.querySelector('[part="rc-purchase-option__badge"]');
    const benefitsEl = root.querySelector('rc-benefits');

    if (templates.banner) {
      rechargeBadge?.style.setProperty('display', 'none', 'important');
      const bannerSlot = upsertMerchSlot(subCard, 'banner', templates.banner, subCard, subCard.firstChild);
      bannerSlot?.querySelector('.rd-rc-merch-banner')?.classList.toggle('is-selected', isSelected);
    }

    if (templates.items) {
      benefitsEl?.style.setProperty('display', 'none', 'important');
    }

    const plansEl = root.querySelector('rc-selling-plans');
    if (templates.badge) {
      upsertMerchSlot(subContainer, 'badge', templates.badge, subContainer, plansEl?.nextElementSibling || null);
    }

    if (templates.items) {
      upsertMerchSlot(subContainer, 'items', templates.items, subContainer, null);
    }

    if (templates.footnote) {
      upsertMerchSlot(subContainer, 'footnote', templates.footnote, subContainer, null);
    }
  }

  /** @param {Element} widget */
  function enhanceWidget(widget) {
    const root = widget.shadowRoot;
    if (!root) return;

    const benefitsEl = root.querySelector('rc-benefits');
    const plansEl = root.querySelector('rc-selling-plans');
    benefitsEl?.style.setProperty('order', '2');
    plansEl?.style.setProperty('order', '1');

    const benefitsRoot = benefitsEl?.shadowRoot;
    benefitsRoot
      ?.querySelector('.rc-learn-more, [part="rc-learn-more__trigger-compact"]')
      ?.style.setProperty('display', 'none', 'important');

    const subContainer = root.querySelector('[part="rc-purchase-option__sub-container"]');
    if (subContainer) {
      const edgePad = window.matchMedia('(max-width: 767.98px)').matches ? 8 : 16;
      const indent = edgePad + 20 + 12;
      subContainer.style.setProperty('padding-left', `${indent}px`, 'important');
      subContainer.style.setProperty('padding-right', `${edgePad}px`, 'important');
      subContainer.style.setProperty('padding-bottom', `${edgePad}px`, 'important');
    }

    const plansRoot = root.querySelector('rc-selling-plans')?.shadowRoot;
    if (plansRoot) {
      ensurePlansSelectStyles(plansRoot);
    }
    plansRoot?.querySelector('[part="rc-plans__label"]')?.style.setProperty('display', 'none', 'important');
    plansRoot?.querySelectorAll('svg.rc-plans-dropdown__icon, .rc-plans-dropdown svg').forEach((icon) => {
      icon.style.setProperty('display', 'none', 'important');
    });

    const plansSelect = plansRoot?.querySelector('[part="rc-plans-select"], select');
    if (plansSelect instanceof HTMLSelectElement) {
      plansSelect.style.setProperty('appearance', 'none', 'important');
      window.RdFrequencySelectFontFit?.register?.(plansSelect);
    }

    const onetimePriceEl = root.querySelector('[part*="onetime"] [part="rc-purchase-option__price"]');
    const subPrices = root.querySelector('[part*="subscription"] [part="rc-purchase-option__prices"]');
    if (onetimePriceEl && subPrices) {
      const compareText = onetimePriceEl.textContent.replace(/\s+/g, ' ').trim();
      if (compareText) {
        let compare = subPrices.querySelector('[data-rd-rc-compare-at]');
        if (!compare) {
          compare = document.createElement('span');
          compare.setAttribute('data-rd-rc-compare-at', '');
          compare.setAttribute('part', 'rc-purchase-option__original-price');
          subPrices.insertBefore(compare, subPrices.firstChild);
        }
        if (compare.textContent !== compareText) {
          compare.textContent = compareText;
        }
      }
    }

    enhanceMerchandising(widget);
  }

  function scan() {
    document.querySelectorAll(WIDGET_SELECTOR).forEach(enhanceWidget);
  }

  let scheduled = false;
  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scan();
    });
  }

  function boot() {
    scan();

    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('variant:update', scheduleScan);
    document.addEventListener('variant:change', scheduleScan);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
